import { useCallback, useEffect, useRef, useState } from 'react';
import { pmaImportAPI } from '../services/api';
import { readDiscoveryException } from '../domain/infra/discoveryProgress';

export default function useLabUserInventory() {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState('');
  const [runMessage, setRunMessage] = useState('');
  const [progress, setProgress] = useState(null);
  const [exception, setException] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const runStartedAt = useRef(0);
  const pollRef = useRef(null);
  const settlingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await pmaImportAPI.getLabUserInventory();
      setInventory(res.data || null);
    } catch (err) {
      setInventory(null);
      setError(err.response?.data?.message || err.message || 'Could not load user inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyResult = useCallback((data) => {
    const probed = Number(data?.probed || 0);
    const sshOk = Number(data?.sshOk || 0);
    const sshFailed = Number(data?.sshFailed || 0);
    const sessions = Number(data?.sessions || 0);
    const vncProcs = Number(data?.vncProcs || 0);
    setRunMessage(
      `Probed ${probed} catalog host${probed === 1 ? '' : 's'} (${sshOk} SSH OK, ${sshFailed} failed). Sessions ${sessions}, VNC processes ${vncProcs}.`
    );
  }, []);

  const captureFinished = useCallback(
    async (next) => {
      if (settlingRef.current) return;
      settlingRef.current = true;
      stopPolling();
      setProgress(next);
      if (next?.result) applyResult(next.result);
      setElapsedMs(Number(next?.elapsedMs) || 0);
      setRunning(false);
      await reload();
    },
    [applyResult, reload, stopPolling]
  );

  const captureFailed = useCallback(
    (next) => {
      if (settlingRef.current) return;
      settlingRef.current = true;
      stopPolling();
      setProgress(next);
      if (next?.error) {
        setException(next.error);
        setRunError(next.error.message);
      }
      setElapsedMs(Number(next?.elapsedMs) || 0);
      setRunning(false);
    },
    [stopPolling]
  );

  const pollProgress = useCallback(async () => {
    try {
      const res = await pmaImportAPI.getLabUserInventoryProgress();
      const next = res.data || null;
      if (!next) return;
      setProgress(next);
      if (next.status === 'running') {
        setRunning(true);
        if (next.startedAt) runStartedAt.current = next.startedAt;
        setElapsedMs(Number(next.elapsedMs) || 0);
        return;
      }
      if (next.status === 'finished') {
        await captureFinished(next);
        return;
      }
      if (next.status === 'failed') {
        captureFailed(next);
      }
    } catch {
      // Progress endpoint may not be deployed yet.
    }
  }, [captureFailed, captureFinished]);

  const watchProgress = useCallback(() => {
    stopPolling();
    pollProgress();
    pollRef.current = setInterval(pollProgress, 1000);
  }, [pollProgress, stopPolling]);

  const runInventory = useCallback(async () => {
    setRunning(true);
    setRunError('');
    setRunMessage('');
    setException(null);
    setElapsedMs(0);
    runStartedAt.current = Date.now();
    settlingRef.current = false;
    watchProgress();
    try {
      const res = await pmaImportAPI.runLabUserInventory();
      if (res.data) setProgress(res.data);
      if (res.data?.status === 'failed' && res.data.error) {
        captureFailed(res.data);
        return;
      }
      watchProgress();
    } catch (err) {
      const parsed = readDiscoveryException(err);
      if (err.response?.status === 409 && parsed.progress) {
        settlingRef.current = false;
        setRunMessage(parsed.message);
        setProgress(parsed.progress);
        if (parsed.progress.startedAt) runStartedAt.current = parsed.progress.startedAt;
        watchProgress();
        return;
      }
      setException(parsed);
      setRunError(parsed.message);
      if (parsed.progress) setProgress(parsed.progress);
      setRunning(false);
      stopPolling();
      if (runStartedAt.current) setElapsedMs(Date.now() - runStartedAt.current);
    }
  }, [captureFailed, stopPolling, watchProgress]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await pmaImportAPI.getLabUserInventoryProgress();
        if (cancelled || !res.data) return;
        const next = res.data;
        setProgress(next);
        if (next.status === 'running') {
          settlingRef.current = false;
          setRunning(true);
          runStartedAt.current = next.startedAt || Date.now();
          setElapsedMs(Number(next.elapsedMs) || 0);
          watchProgress();
          return;
        }
        if (next.status === 'failed' && next.error) {
          setException(next.error);
          setRunError(next.error.message);
          setElapsedMs(Number(next.elapsedMs) || 0);
        }
        if (next.status === 'finished' && next.result) {
          applyResult(next.result);
          setElapsedMs(Number(next.elapsedMs) || 0);
        }
      } catch {
        // Progress endpoint may not be deployed yet.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyResult, watchProgress]);

  useEffect(() => {
    if (!running) return undefined;
    const tick = setInterval(() => {
      if (runStartedAt.current) setElapsedMs(Date.now() - runStartedAt.current);
    }, 250);
    return () => clearInterval(tick);
  }, [running]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  return {
    inventory,
    loading,
    error,
    running,
    runError,
    runMessage,
    runInventory,
    progress,
    exception,
    elapsedMs,
  };
}
